import type { ChangeEvent, DragEvent, ReactNode } from "react";
import { useRef, useState } from "react";
import { CircularProgress, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import CloudUpload from "@mui/icons-material/CloudUpload";
import { useTranslation } from "react-i18next";

type DragDropFileUploadProps = {
    onFilesSelected: (files: File[]) => void;
    disabled?: boolean;
    accept?: string;
    children?: ReactNode;
    multiple?: boolean;
    isUploading?: boolean;
    sx?: SxProps<Theme>;
};

export const DragDropFileUpload = (props: DragDropFileUploadProps) => {
    const { onFilesSelected, disabled = false, accept, children, multiple = true, isUploading = false, sx } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const filesArray = Array.from(event.target.files);
            onFilesSelected(filesArray);

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragging(true);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!disabled && e.currentTarget.contains(e.relatedTarget as Node)) {
            return;
        }

        if (!disabled) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (!disabled && e.dataTransfer.files) {
            const filesArray = Array.from(e.dataTransfer.files);

            if (accept) {
                const acceptedTypes = accept.split(",").map((type) => type.trim());
                const validFiles = filesArray.filter((file) => {
                    return acceptedTypes.some((acceptedType) => {
                        if (acceptedType.startsWith(".")) {
                            const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
                            return extension === acceptedType.toLowerCase();
                        }

                        if (acceptedType.endsWith("/*")) {
                            const baseType = acceptedType.replace("/*", "");
                            return file.type.startsWith(baseType);
                        }

                        return file.type === acceptedType;
                    });
                });

                if (validFiles.length > 0) {
                    onFilesSelected(validFiles);

                    if (validFiles.length < filesArray.length) {
                        console.warn(
                            `${filesArray.length - validFiles.length} files were filtered out because they don't match the accepted types.`,
                        );
                    }
                } else {
                    console.warn("No valid files were found in the drop. Please check the file types.");
                }
            } else {
                onFilesSelected(filesArray);
            }
        }
    };

    const triggerFileInput = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <>
            <input
                type="file"
                style={{ display: "none" }}
                onChange={handleFileSelect}
                ref={fileInputRef}
                disabled={disabled}
                accept={accept}
                multiple={multiple}
            />
            <Stack
                ref={dropZoneRef}
                alignItems="center"
                justifyContent="center"
                sx={{
                    border:
                        isDragging ?
                            `2px dashed ${theme.palette.primary.main}`
                        :   `2px dashed ${theme.palette.grey[700]}`,
                    borderRadius: theme.shape.borderRadius,
                    p: theme.spacing(2),
                    textAlign: "center",
                    cursor: disabled ? "default" : "pointer",
                    bgcolor: isDragging ? theme.palette.action.hover : theme.palette.grey[200],
                    "&:hover": {
                        bgcolor: disabled ? "transparent" : theme.palette.grey[400],
                    },
                    transition: "all 0.2s ease",
                    minHeight: "300px",
                    ...sx,
                }}
                onClick={disabled ? undefined : triggerFileInput}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {children ||
                    (isUploading ?
                        <Stack alignItems="center" justifyContent="center" spacing={1}>
                            <CircularProgress />
                            <Typography
                                variant="body1"
                                fontWeight={theme.typography.fontWeightMedium}
                                mb={theme.spacing(1)}
                            >
                                {t("documentation.uploading", "Uploading...")}
                            </Typography>
                        </Stack>
                    :   <>
                            <CloudUpload
                                sx={{
                                    fontSize: 40,
                                    color: theme.palette.primary.main,
                                    mb: theme.spacing(1),
                                }}
                            />
                            <Typography
                                variant="body1"
                                fontWeight={theme.typography.fontWeightMedium}
                                mb={theme.spacing(1)}
                            >
                                {isDragging ?
                                    t("common.drop_files_here", "Drop files here")
                                :   t("documentation.upload", "Upload")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {!isDragging && t("common.drag_drop_or_click", "Drag and drop or click")}
                            </Typography>
                        </>)}
            </Stack>
        </>
    );
};
